import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Phone, ArrowLeft, CheckCircle, Eye, EyeOff, KeyRound } from 'lucide-react';
import apiClient from '../../services/apiClient';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_tutor-contact-access/artifacts/ofobdr0d_AVYUDHA_LOGO_2-removebg-preview.png';

export const ForgotPassword = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1=phone, 2=OTP+newPassword, 3=success
    const [phone, setPhone] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // OTP state
    const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
    const [timer, setTimer] = useState(0);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [otpSending, setOtpSending] = useState(false);
    const otpInputRefs = useRef([]);

    // New password state
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Timer countdown
    useEffect(() => {
        let interval;
        if (timer > 0) {
            interval = setInterval(() => setTimer(t => t - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [timer]);

    // Resend cooldown
    useEffect(() => {
        let interval;
        if (resendCooldown > 0) {
            interval = setInterval(() => setResendCooldown(t => t - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [resendCooldown]);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    // Step 1: Send OTP
    const handleSendOtp = async (e) => {
        e?.preventDefault();
        setError('');

        if (!phone.trim()) {
            setError('Please enter your phone number');
            return;
        }

        setOtpSending(true);
        try {
            await apiClient.post('/auth/forgot-password/send-otp', { phone });
            setStep(2);
            setTimer(300);
            setResendCooldown(30);
            setOtpValues(['', '', '', '', '', '']);
            setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
        } finally {
            setOtpSending(false);
        }
    };

    // Resend OTP
    const handleResendOtp = async () => {
        if (resendCooldown > 0) return;
        setError('');
        setOtpSending(true);
        try {
            await apiClient.post('/auth/forgot-password/send-otp', { phone });
            setTimer(300);
            setResendCooldown(30);
            setOtpValues(['', '', '', '', '', '']);
            otpInputRefs.current[0]?.focus();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to resend OTP.');
        } finally {
            setOtpSending(false);
        }
    };

    // OTP input handlers
    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const newValues = [...otpValues];
        newValues[index] = value.slice(-1);
        setOtpValues(newValues);
        if (value && index < 5) {
            otpInputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
            otpInputRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pasted.length > 0) {
            const newValues = [...otpValues];
            for (let i = 0; i < 6; i++) {
                newValues[i] = pasted[i] || '';
            }
            setOtpValues(newValues);
            const focusIndex = Math.min(pasted.length, 5);
            otpInputRefs.current[focusIndex]?.focus();
        }
    };

    // Step 2: Verify OTP + Reset Password
    const handleResetPassword = async (e) => {
        e.preventDefault();
        const otpCode = otpValues.join('');

        if (otpCode.length !== 6) {
            setError('Please enter the complete 6-digit OTP');
            return;
        }
        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setError('');
        setLoading(true);
        try {
            await apiClient.post('/auth/forgot-password/reset', {
                phone,
                otp: otpCode,
                new_password: newPassword
            });
            setStep(3);
        } catch (err) {
            setError(err.response?.data?.detail || 'Password reset failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#041f96]/5 via-white to-[#041f96]/5 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="space-y-1 text-center">
                    <div className="flex justify-center mb-4">
                        <img src={LOGO_URL} alt="Avyudha" className="h-14 object-contain" />
                    </div>
                    <CardTitle className="text-2xl font-bold">
                        {step === 1 && 'Forgot Password'}
                        {step === 2 && 'Reset Password'}
                        {step === 3 && 'Password Reset!'}
                    </CardTitle>
                    <CardDescription>
                        {step === 1 && 'Enter your registered phone number to receive an OTP'}
                        {step === 2 && `Enter the OTP sent to ${phone} and set your new password`}
                        {step === 3 && 'Your password has been reset successfully'}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {/* ===== STEP 1: Enter Phone ===== */}
                    {step === 1 && (
                        <form onSubmit={handleSendOtp} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="phone">Phone Number</Label>
                                <Input
                                    id="phone"
                                    type="tel"
                                    placeholder="+91 98765 43210"
                                    value={phone}
                                    onChange={(e) => { setPhone(e.target.value); setError(''); }}
                                    required
                                />
                            </div>

                            <Button
                                type="submit"
                                className="w-full bg-[#041f96] hover:bg-[#031580]"
                                disabled={otpSending}
                            >
                                {otpSending ? (
                                    <span className="flex items-center gap-2">
                                        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                        Sending OTP...
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        <Phone className="h-4 w-4" />
                                        Send OTP
                                    </span>
                                )}
                            </Button>

                            <p className="text-center text-sm text-gray-600">
                                Remember your password?{' '}
                                <Link to="/login" className="text-[#041f96] hover:underline font-medium">
                                    Login
                                </Link>
                            </p>
                        </form>
                    )}

                    {/* ===== STEP 2: OTP + New Password ===== */}
                    {step === 2 && (
                        <form onSubmit={handleResetPassword} className="space-y-5">
                            <button
                                type="button"
                                onClick={() => { setStep(1); setError(''); }}
                                className="flex items-center text-sm text-gray-500 hover:text-gray-800 transition"
                            >
                                <ArrowLeft className="h-4 w-4 mr-1" /> Change phone number
                            </button>

                            {/* OTP Input */}
                            <div>
                                <Label className="mb-2 block">Enter OTP</Label>
                                <div className="flex justify-center gap-2 sm:gap-3">
                                    {otpValues.map((val, i) => (
                                        <input
                                            key={i}
                                            ref={el => otpInputRefs.current[i] = el}
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={1}
                                            value={val}
                                            onChange={(e) => handleOtpChange(i, e.target.value)}
                                            onKeyDown={(e) => handleOtpKeyDown(i, e)}
                                            onPaste={i === 0 ? handleOtpPaste : undefined}
                                            className="w-11 h-13 sm:w-12 sm:h-14 text-center text-xl font-bold border-2 rounded-lg
                        focus:border-[#041f96] focus:ring-2 focus:ring-[#041f96]/20 outline-none
                        transition-all duration-200"
                                            style={{ fontSize: '1.25rem' }}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Timer & Resend */}
                            <div className="text-center space-y-1">
                                {timer > 0 ? (
                                    <p className="text-sm text-gray-500">
                                        OTP expires in <span className="font-semibold text-[#041f96]">{formatTime(timer)}</span>
                                    </p>
                                ) : (
                                    <p className="text-sm text-red-500 font-medium">OTP expired. Please request a new one.</p>
                                )}
                                <button
                                    type="button"
                                    onClick={handleResendOtp}
                                    disabled={resendCooldown > 0 || otpSending}
                                    className={`text-sm font-medium ${resendCooldown > 0
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-[#041f96] hover:underline cursor-pointer'
                                        }`}
                                >
                                    {otpSending
                                        ? 'Sending...'
                                        : resendCooldown > 0
                                            ? `Resend OTP in ${resendCooldown}s`
                                            : 'Resend OTP'}
                                </button>
                            </div>

                            {/* New Password Fields */}
                            <div className="space-y-2">
                                <Label htmlFor="newPassword">New Password</Label>
                                <div className="relative">
                                    <Input
                                        id="newPassword"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4 text-gray-500" /> : <Eye className="h-4 w-4 text-gray-500" />}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                />
                            </div>

                            <Button
                                type="submit"
                                className="w-full bg-[#041f96] hover:bg-[#031580]"
                                disabled={loading || otpValues.join('').length !== 6 || timer === 0}
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">
                                        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                        Resetting Password...
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        <KeyRound className="h-4 w-4" />
                                        Reset Password
                                    </span>
                                )}
                            </Button>
                        </form>
                    )}

                    {/* ===== STEP 3: Success ===== */}
                    {step === 3 && (
                        <div className="text-center space-y-4">
                            <div className="flex justify-center">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                    <CheckCircle className="h-8 w-8 text-green-600" />
                                </div>
                            </div>
                            <p className="text-gray-600">
                                Your password has been reset successfully. You can now login with your new password.
                            </p>
                            <Button
                                onClick={() => navigate('/login')}
                                className="w-full bg-[#041f96] hover:bg-[#031580]"
                            >
                                Go to Login
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
